import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Zap } from "lucide-react";

const pathways = [
  {
    id: 1,
    badge: "Pathway 1",
    headline: "Build Your Foundation",
    audience: "For fresh candidates with limited industry or subject matter knowledge",
    description:
      "The book provides a comprehensive foundation, while the subscription delivers instructor-led video tutorials, mock exams, and the question bank for active practice and exam readiness.",
    components: [
      { label: "Amazon Book", price: "$299.99", detail: "Comprehensive study guide", icon: "book" },
      {
        label: "Monthly Subscription",
        price: "$29.99/month",
        detail: "Video tutorials · Mock exams · Question bank",
        icon: "graduation",
      },
    ],
    accent: "#1e40af",
    accentLight: "#dbeafe",
    accentMid: "#93c5fd",
  },
  {
    id: 2,
    badge: "Pathway 2",
    headline: "Accelerate Your Prep",
    audience:
      "For candidates with a finance degree or relevant industry experience",
    description:
      "The interactive quick study guide delivers a focused review of core concepts, while the subscription provides video tutorials, mock exams, and the question bank to sharpen your exam readiness.",
    components: [
      {
        label: "Online Interactive Quick Study Guide",
        price: "$34.99 / $49.99",
        detail: "Focused concept review",
        icon: "zap",
      },
      {
        label: "Monthly Subscription",
        price: "$29.99/month",
        detail: "Video tutorials · Mock exams · Question bank",
        icon: "graduation",
      },
    ],
    accent: "#166534",
    accentLight: "#dcfce7",
    accentMid: "#86efac",
  },
];

const IconComponent = ({ iconName, size = 24, color }) => {
  switch (iconName) {
    case "book":
      return <BookOpen size={size} color={color} />;
    case "graduation":
      return <GraduationCap size={size} color={color} />;
    case "zap":
      return <Zap size={size} color={color} />;
    default:
      return <BookOpen size={size} color={color} />;
  }
};

export default function VittaPathways() {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div style={styles.wrapper}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700;800&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

        .pathway-card {
          transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .pathway-card:hover {
          transform: translateY(-6px);
        }
        .component-row {
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .component-row:hover {
          transform: translateX(4px);
        }
        .pathway-badge {
          animation: fadeSlideIn 0.5s ease forwards;
          opacity: 0;
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes subtlePulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.7; }
        }
        @media (max-width: 768px) {
          .pathways-grid {
            grid-template-columns: 1fr !important;
          }
          .pathways-cta-grid {
            grid-template-columns: 1fr !important;
          }
        }
        .pathway-cta-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
        }
      `}</style>

      {/* Section Header */}
      <div style={styles.headerBlock}>
        <p style={styles.overline}>HOW TO GET STARTED</p>
        <h2 style={styles.sectionTitle}>Choose Your Study Pathway</h2>
        <p style={styles.sectionSubtitle}>
          Select the pathway that matches your background — both lead to full exam readiness.
        </p>
      </div>

      {/* Cards */}
      <div className="pathways-grid" style={styles.grid}>
        {pathways.map((pw) => {
          const isHovered = hoveredCard === pw.id;
          return (
            <div
              key={pw.id}
              className="pathway-card"
              onMouseEnter={() => setHoveredCard(pw.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                ...styles.card,
                boxShadow: isHovered
                  ? `0 20px 60px -12px ${pw.accent}22, 0 8px 24px -8px rgba(0,0,0,0.08)`
                  : "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
                borderColor: isHovered ? pw.accentMid : "#e2e8f0",
              }}
            >
              {/* Decorative corner accent */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 120,
                  height: 120,
                  background: `radial-gradient(circle at top right, ${pw.accentLight}, transparent 70%)`,
                  borderRadius: "0 16px 0 0",
                  animation: isHovered ? "subtlePulse 2.5s ease infinite" : "none",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
              />

              {/* Badge */}
              <div
                className="pathway-badge"
                style={{
                  ...styles.badge,
                  backgroundColor: pw.accent,
                }}
              >
                {pw.badge}
              </div>

              {/* Headline */}
              <h3 style={styles.cardHeadline}>{pw.headline}</h3>

              {/* Audience tag */}
              <div
                style={{
                  ...styles.audienceTag,
                  backgroundColor: pw.accentLight,
                  color: pw.accent,
                  borderLeft: `3px solid ${pw.accent}`,
                }}
              >
                {pw.audience}
              </div>

              {/* Description */}
              <p style={styles.cardDescription}>{pw.description}</p>

              {/* Divider */}
              <div style={styles.divider}>
                <div
                  style={{
                    ...styles.dividerLine,
                    background: `linear-gradient(90deg, ${pw.accent}30, transparent)`,
                  }}
                />
                <span style={{ ...styles.dividerLabel, color: pw.accent }}>
                  INCLUDES
                </span>
                <div
                  style={{
                    ...styles.dividerLine,
                    background: `linear-gradient(90deg, transparent, ${pw.accent}30)`,
                  }}
                />
              </div>

              {/* Components */}
              <div style={styles.componentsList}>
                {pw.components.map((comp, idx) => (
                  <div key={idx}>
                    <div
                      className="component-row"
                      style={{
                        ...styles.componentRow,
                        backgroundColor: idx % 2 === 0 ? `${pw.accentLight}88` : "transparent",
                      }}
                    >
                      <div style={styles.componentIcon}>
                        <IconComponent iconName={comp.icon} size={24} color={pw.accent} />
                      </div>
                      <div style={styles.componentInfo}>
                        <div style={styles.componentLabelRow}>
                          <span style={styles.componentLabel}>{comp.label}</span>
                          {comp.price && (
                            <span
                              style={{
                                ...styles.priceTag,
                                backgroundColor: pw.accent,
                              }}
                            >
                              {comp.price}
                            </span>
                          )}
                        </div>
                        <span style={styles.componentDetail}>{comp.detail}</span>
                      </div>
                    </div>
                    {idx < pw.components.length - 1 && (
                      <div style={styles.plusConnector}>
                        <div
                          style={{
                            ...styles.plusIcon,
                            backgroundColor: pw.accentLight,
                            color: pw.accent,
                            border: `1.5px solid ${pw.accent}40`,
                          }}
                        >
                          +
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    fontFamily: "'Source Sans 3', sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: "56px 24px",
    backgroundColor: "#f8fafc",
  },
  headerBlock: {
    textAlign: "center",
    marginBottom: 48,
  },
  overline: {
    fontFamily: "'Source Sans 3', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.15em",
    color: "#1e40af",
    marginBottom: 10,
    textTransform: "uppercase",
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 36,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 12px",
    lineHeight: 1.2,
  },
  sectionSubtitle: {
    fontSize: 16,
    color: "#64748b",
    margin: 0,
    fontWeight: 400,
    maxWidth: 520,
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: 1.55,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 32,
    alignItems: "start",
    marginBottom: 48,
  },
  card: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1.5px solid #e2e8f0",
    padding: "36px 32px 32px",
    cursor: "default",
  },
  badge: {
    display: "inline-block",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "5px 14px",
    borderRadius: 20,
    marginBottom: 18,
  },
  cardHeadline: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 14px",
    lineHeight: 1.25,
  },
  audienceTag: {
    fontSize: 13.5,
    fontWeight: 600,
    padding: "10px 16px",
    borderRadius: "0 10px 10px 0",
    marginBottom: 16,
    lineHeight: 1.45,
  },
  cardDescription: {
    fontSize: 14.5,
    color: "#475569",
    lineHeight: 1.65,
    margin: "0 0 22px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
  },
  componentsList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  componentRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 12,
  },
  componentIcon: {
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
  },
  componentInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flex: 1,
  },
  componentLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  componentLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },
  priceTag: {
    fontSize: 11,
    fontWeight: 700,
    color: "#ffffff",
    padding: "3px 10px",
    borderRadius: 6,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  componentDetail: {
    fontSize: 13,
    color: "#64748b",
    fontWeight: 400,
  },
  plusConnector: {
    display: "flex",
    justifyContent: "center",
    padding: "4px 0",
  },
  plusIcon: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
  },
  ctaSection: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
    maxWidth: 800,
    margin: "0 auto",
  },
  ctaButton: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "24px 28px",
    backgroundColor: "#ffffff",
    border: "2px solid #e2e8f0",
    borderRadius: 16,
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)",
  },
  ctaIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 56,
    height: 56,
    backgroundColor: "#f1f5f9",
    borderRadius: 12,
    flexShrink: 0,
  },
  ctaContent: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  ctaTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  },
  ctaSubtitle: {
    fontSize: 14,
    color: "#64748b",
  },
};
